import { useEffect, useRef, useState } from 'react';
import {
  startChatAgentSession,
  sendChatMessage,
  endChat,
  type AgentChatMessage,
} from '../api/endpoints.js';
import { subscribeAgentStream } from '../connect/agentStream.js';
import { errorMessage } from '../api/client.js';

/**
 * Live chat composer for the CRM Conversation page — lets an agent reply to a website
 * visitor without the Connect CCP. It joins the chat (a CUSTOM_BOT "Support" participant
 * via the backend), shows the running transcript, receives inbound customer messages in
 * real time over the agent SSE stream, and sends replies through the backend.
 */
export function ChatComposer({
  conversationId,
  contactId,
}: {
  conversationId: string;
  contactId: string | null;
}) {
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [displayName, setDisplayName] = useState<string>('Support');
  const [status, setStatus] = useState<'connecting' | 'ready' | 'error'>('connecting');
  const [errText, setErrText] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [ended, setEnded] = useState(false);
  const seen = useRef<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement | null>(null);

  const key = (m: { sender: string; at: string; body: string }): string =>
    `${m.sender}|${m.at}|${m.body}`;

  const addMessages = (incoming: AgentChatMessage[]): void => {
    setMessages((prev) => {
      const next = [...prev];
      for (const m of incoming) {
        const k = key(m);
        if (seen.current.has(k)) continue;
        seen.current.add(k);
        next.push(m);
      }
      next.sort((a, b) => a.at.localeCompare(b.at));
      return next;
    });
  };

  // Join the chat + load transcript.
  useEffect(() => {
    let cancelled = false;
    setStatus('connecting');
    startChatAgentSession(conversationId)
      .then((s) => {
        if (cancelled) return;
        setDisplayName(s.displayName);
        addMessages(s.messages);
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setErrText(errorMessage(e));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Receive inbound customer messages (and agent echoes) in real time.
  useEffect(() => {
    const off = subscribeAgentStream({
      onWaiting: () => {},
      onCleared: () => {},
      onMessage: (m) => {
        if (m.conversationId !== conversationId) return;
        addMessages([
          { sender: m.sender, displayName: m.displayName, body: m.body, at: m.at },
        ]);
      },
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length]);

  const send = async (): Promise<void> => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    try {
      await sendChatMessage(conversationId, text);
      // The message echoes back over SSE; nothing to append here.
    } catch (e) {
      setErrText(errorMessage(e));
      setInput(text); // restore so the agent can retry
    } finally {
      setSending(false);
    }
  };

  const end = async (): Promise<void> => {
    if (ended) return;
    if (!window.confirm('End this chat for the visitor?')) return;
    setEnded(true);
    try {
      await endChat(conversationId);
      setStatus('error'); // collapses the composer to the "ended" notice
    } catch (e) {
      setErrText(errorMessage(e));
      setEnded(false);
    }
  };

  if (!contactId) return null;

  return (
    <section className="card chat-live">
      <div className="chat-live__header">
        <h3>Live chat</h3>
        <span className={`badge badge--${status === 'ready' ? 'chat' : 'agent'}`}>
          {status === 'ready' ? `Connected as ${displayName}` : status === 'error' ? 'Unavailable' : 'Connecting…'}
        </span>
        {status === 'ready' && (
          <button
            type="button"
            className="btn btn--secondary btn--sm chat-live__end"
            disabled={ended}
            onClick={() => void end()}
            title="Disconnect support and end the chat for the visitor"
          >
            End chat
          </button>
        )}
      </div>
      {status === 'error' ? (
        <p className="muted">Couldn’t join this chat{errText ? `: ${errText}` : ''}. It may have ended.</p>
      ) : (
        <>
          <div className="chat-live__messages" ref={listRef}>
            {messages.length === 0 ? (
              <p className="muted chat-live__empty">No messages yet.</p>
            ) : (
              messages.map((m, i) => (
                <div key={`${key(m)}-${i}`} className={`chat-live__msg chat-live__msg--${m.sender}`}>
                  <span className="chat-live__msg-name">
                    {m.sender === 'customer' ? m.displayName || 'Visitor' : m.sender === 'agent' ? 'You' : 'System'}
                  </span>
                  <span className="chat-live__msg-body">{m.body}</span>
                </div>
              ))
            )}
          </div>
          <div className="chat-live__composer">
            <input
              type="text"
              placeholder="Type a reply to the visitor…"
              value={input}
              disabled={status !== 'ready'}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={status !== 'ready' || sending || !input.trim()}
              onClick={() => void send()}
            >
              Send
            </button>
          </div>
        </>
      )}
    </section>
  );
}
